import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatMoney, creditsForPrice, getCurrencyMultiplier } from '@/lib/money';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { trackEvent } from '@/lib/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface PlanInfo {
  plan: string;
  price: number;
  features: string[];
}

interface Pricing {
  currency: string;
  gateway: string;
  plans: PlanInfo[];
}

interface Subscription {
  plan: string;
  status: string;
  gateway?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

interface CreditInfo {
  balance: number;
  currency?: string;
  transactions: Array<{
    type: string;
    amount: number;
    balance: number;
    description: string;
    createdAt: string;
  }>;
}

interface Statement {
  currency: string;
  total: number;
  lineItems: { description: string; total: number; kind: string }[];
}

// Credits and statement are not team-id scoped server-side (they resolve the
// current team from the auth context), so they use flat local keys until they
// earn a slot in the qk factory.
const CREDITS_KEY = ['billing', 'credits'] as const; // TODO: promote to qk
const STATEMENT_KEY = ['billing', 'statement'] as const; // TODO: promote to qk

export default function BillingSettingsPage() {
  const queryClient = useQueryClient();

  const [voucherCode, setVoucherCode] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    type: 'credits' | 'cancel';
    plan?: string;
    creditsNeeded?: number;
  } | null>(null);

  // Handle checkout redirect result (one-shot on mount).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      toast.success('Payment successful! Your plan will be activated shortly.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkout === 'cancelled') {
      toast.info('Checkout was cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Resolve the current team so pricing comes back in the team's LOCKED
  // currency, not the viewer's geo. Otherwise a SUPER_ADMIN in Canada looking
  // at an Indian customer's billing page would see CAD prices.
  const teamQuery = useQuery({
    queryKey: qk.team('me'),
    queryFn: async () => {
      try {
        const res = await api.get('/teams/me');
        return (res.data?.data ?? null) as { id?: string; _id?: string } | null;
      } catch {
        return null;
      }
    },
  });
  const teamId = teamQuery.data?.id || teamQuery.data?._id;

  const pricingQuery = useQuery({
    queryKey: qk.pricing(teamId ?? 'me'),
    queryFn: async () => {
      const pricingQs = teamId ? `?teamId=${encodeURIComponent(teamId)}` : '';
      return (await api.get(`/billing/pricing${pricingQs}`)).data.data as Pricing;
    },
    // Wait for the team lookup to settle so the teamId (and thus the locked
    // currency) is known before the first pricing request.
    enabled: !teamQuery.isPending,
  });

  const subscriptionQuery = useQuery({
    queryKey: qk.teamBilling(teamId ?? 'me'),
    queryFn: async () => {
      try {
        return (await api.get('/billing/subscription')).data.data as Subscription;
      } catch {
        return { plan: 'free', status: 'active' } as Subscription;
      }
    },
  });

  const creditsQuery = useQuery({
    queryKey: CREDITS_KEY,
    queryFn: async () => {
      try {
        return (await api.get('/billing/credits')).data.data as CreditInfo;
      } catch {
        return { balance: 0, transactions: [] } as CreditInfo;
      }
    },
  });

  const statementQuery = useQuery({
    queryKey: STATEMENT_KEY,
    queryFn: async () => {
      try {
        return ((await api.get('/billing/statement')).data.data ?? null) as Statement | null;
      } catch {
        return null;
      }
    },
  });

  // Pricing is the only read without a graceful in-queryFn fallback, so surface
  // its failure the way the old single catch did.
  useEffect(() => {
    if (pricingQuery.isError) {
      toast.error(apiErrorMessage(pricingQuery.error, 'Failed to load billing information'));
    }
  }, [pricingQuery.isError, pricingQuery.error]);

  const invalidateBilling = () => {
    queryClient.invalidateQueries({ queryKey: qk.teamBilling(teamId ?? 'me') });
    queryClient.invalidateQueries({ queryKey: CREDITS_KEY });
    queryClient.invalidateQueries({ queryKey: STATEMENT_KEY });
  };

  const redeemMutation = useMutation({
    mutationFn: async (code: string) =>
      (await api.post('/billing/credits/redeem', { code })).data.data,
    onSuccess: (data) => {
      toast.success(`Redeemed! Added ${data.credits} credits.`);
      setVoucherCode('');
      invalidateBilling();
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to redeem voucher'));
    },
  });

  const upgradeMutation = useMutation({
    mutationFn: async (plan: string) => (await api.post('/billing/subscribe', { plan })).data.data,
    onSuccess: (data, plan) => {
      const checkoutUrl = data?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      toast.success(`Upgraded to ${plan}!`);
      invalidateBilling();
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to start subscription'));
    },
  });

  const payWithCreditsMutation = useMutation({
    mutationFn: async (plan: string) =>
      (await api.post('/billing/subscribe-with-credits', { plan })).data.data,
    onSuccess: (data) => {
      toast.success(
        `Subscribed to ${data.plan}! ${data.creditsUsed.toLocaleString()} credits used.`
      );
      invalidateBilling();
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to pay with credits'));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => (await api.post('/billing/subscription/cancel')).data.data,
    onSuccess: () => {
      toast.success('Subscription will be cancelled at period end');
      invalidateBilling();
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to cancel subscription'));
    },
  });

  function handleRedeemVoucher(e: React.FormEvent) {
    e.preventDefault();
    if (!voucherCode) return;
    redeemMutation.mutate(voucherCode);
  }

  function handleUpgrade(plan: string) {
    trackEvent('upgrade_clicked', { source: 'billing_settings', target_plan: plan });
    upgradeMutation.mutate(plan);
  }

  function promptPayWithCredits(plan: string, creditsNeeded: number) {
    setConfirmDialog({ type: 'credits', plan, creditsNeeded });
  }

  function executePayWithCredits() {
    if (!confirmDialog || confirmDialog.type !== 'credits' || !confirmDialog.plan) return;
    const plan = confirmDialog.plan;
    setConfirmDialog(null);
    payWithCreditsMutation.mutate(plan);
  }

  function promptCancelSubscription() {
    setConfirmDialog({ type: 'cancel' });
  }

  function executeCancelSubscription() {
    setConfirmDialog(null);
    cancelMutation.mutate();
  }

  const isUpgrading = (plan: string) =>
    upgradeMutation.isPending && upgradeMutation.variables === plan;
  const isPayingWithCredits = (plan: string) =>
    payWithCreditsMutation.isPending && payWithCreditsMutation.variables === plan;

  // Skeleton only while a read has no data yet — background refetches keep the
  // last-loaded values on screen.
  if (
    pricingQuery.isPending ||
    subscriptionQuery.isPending ||
    creditsQuery.isPending ||
    statementQuery.isPending
  ) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const pricing = pricingQuery.data ?? null;
  const subscription = subscriptionQuery.data ?? null;
  const credits = creditsQuery.data ?? null;
  const statement = statementQuery.data ?? null;

  const creditBalance = credits?.balance ?? 0;
  const creditCurrency = credits?.currency ?? pricing?.currency;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        <p className="text-gray-500">Manage your plan, payment methods, and credits.</p>
      </div>

      {/* Current Plan */}
      <div className="rounded-lg border p-6">
        <h2 className="mb-2 text-lg font-semibold">Current Plan</h2>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium capitalize text-blue-800">
            {subscription?.plan ?? 'Free'}
          </span>
          <span className="text-sm text-gray-500">Status: {subscription?.status ?? 'active'}</span>
          {subscription?.currentPeriodEnd && (
            <span className="text-sm text-gray-500">
              Renews: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </span>
          )}
        </div>
        {subscription?.plan !== 'free' && !subscription?.cancelAtPeriodEnd && (
          <button
            onClick={promptCancelSubscription}
            className="mt-3 text-sm text-red-600 hover:text-red-700"
          >
            Cancel subscription
          </button>
        )}
        {subscription?.cancelAtPeriodEnd && (
          <p className="mt-3 text-sm text-amber-600">
            Subscription will be cancelled at period end.
          </p>
        )}
      </div>

      {/* This month's charges — unified statement (plan + plugins + add-ons) */}
      {statement && statement.lineItems?.length > 0 && (
        <div className="rounded-lg border p-6">
          <h2 className="mb-3 text-lg font-semibold">This month&apos;s charges</h2>
          <div className="divide-y">
            {statement.lineItems.map((li, i) => (
              <div
                key={i}
                className={`flex items-center justify-between py-2 text-sm ${li.kind === 'plan' ? 'font-medium' : ''}`}
              >
                <span>{li.description}</span>
                <span>{formatMoney(li.total, statement.currency)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-2 text-sm font-semibold">
              <span>Total</span>
              <span>{formatMoney(statement.total, statement.currency)}</span>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Each subscription is billed to its own payment method and renews on its own date.
          </p>
        </div>
      )}

      {/* Plans */}
      {pricing && (
        <div>
          <h2 className="mb-4 text-lg font-semibold">Available Plans</h2>
          <div className="grid gap-4 md:grid-cols-4">
            {pricing.plans.map((plan) => {
              const isCurrentPlan = subscription?.plan === plan.plan;
              const isPaidPlan = !isCurrentPlan && plan.plan !== 'free';
              // plan.price is in minor units (paise/cents); 1 credit = 1 major unit.
              const priceInCredits = creditsForPrice(plan.price, pricing.currency);
              const hasEnoughCredits = creditBalance >= priceInCredits;
              const shortfall = priceInCredits - creditBalance;

              return (
                <div
                  key={plan.plan}
                  className={`flex flex-col rounded-lg border p-5 ${
                    isCurrentPlan ? 'border-blue-500 ring-2 ring-blue-200' : ''
                  }`}
                >
                  <h3 className="text-lg font-bold capitalize">{plan.plan}</h3>
                  <p className="mt-1 text-2xl font-bold">
                    {plan.price === 0 ? 'Free' : formatMoney(plan.price, pricing.currency)}
                    {plan.price > 0 && (
                      <span className="text-sm font-normal text-gray-500">/mo</span>
                    )}
                  </p>
                  <ul className="mt-3 flex-1 space-y-1 text-sm text-gray-600">
                    {plan.features.map((f) => (
                      <li key={f}>&#10003; {f}</li>
                    ))}
                  </ul>

                  {isPaidPlan ? (
                    <div className="mt-4 space-y-2">
                      {/* Pay with card */}
                      <button
                        onClick={() => handleUpgrade(plan.plan)}
                        disabled={isUpgrading(plan.plan)}
                        className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isUpgrading(plan.plan) ? 'Redirecting...' : 'Pay with Card'}
                      </button>

                      {/* Pay with credits — always visible */}
                      {hasEnoughCredits ? (
                        <button
                          onClick={() => promptPayWithCredits(plan.plan, priceInCredits)}
                          disabled={isPayingWithCredits(plan.plan)}
                          className="w-full rounded-md border border-green-600 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
                        >
                          {isPayingWithCredits(plan.plan)
                            ? 'Processing...'
                            : `Pay with Credits (${priceInCredits.toLocaleString()} credits)`}
                        </button>
                      ) : (
                        <div className="w-full rounded-md border border-dashed border-gray-300 px-3 py-2 text-center text-sm text-gray-400">
                          <span>Pay with Credits</span>
                          <span className="mt-0.5 block text-xs">
                            Need {shortfall.toLocaleString()} more credits
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 h-[36px]" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Credits */}
      <div className="rounded-lg border p-6">
        <h2 className="mb-2 text-lg font-semibold">Credits</h2>
        <p className="text-3xl font-bold">{creditBalance.toLocaleString()}</p>
        <p className="text-sm text-gray-500">
          Available credits
          {creditCurrency && creditBalance > 0 && (
            <>
              {' · worth ≈ '}
              {formatMoney(creditBalance * getCurrencyMultiplier(creditCurrency), creditCurrency)}
            </>
          )}
        </p>
        <p className="mt-1 text-xs text-gray-400">
          1 credit = 1 {creditCurrency ?? 'unit'} of plan cost
        </p>

        <form onSubmit={handleRedeemVoucher} className="mt-4 flex gap-3">
          <input
            type="text"
            placeholder="Voucher code"
            value={voucherCode}
            onChange={(e) => setVoucherCode(e.target.value)}
            className="flex-1 rounded-md border px-3 py-2"
          />
          <button
            type="submit"
            disabled={redeemMutation.isPending}
            className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50"
          >
            {redeemMutation.isPending ? 'Redeeming...' : 'Redeem'}
          </button>
        </form>

        {credits && credits.transactions.length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-medium text-gray-500">Recent Transactions</h3>
            <div className="divide-y rounded-md border">
              {credits.transactions.slice(0, 10).map((tx, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span>{tx.description}</span>
                  <span className={tx.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                    {tx.amount > 0 ? '+' : ''}
                    {tx.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={Boolean(confirmDialog)}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
      >
        <DialogContent className="sm:max-w-md">
          {confirmDialog?.type === 'credits' && (
            <>
              <DialogHeader>
                <DialogTitle>Pay with Credits</DialogTitle>
                <DialogDescription>
                  Use {confirmDialog.creditsNeeded?.toLocaleString()} credits from your balance to
                  subscribe to the{' '}
                  <span className="font-medium capitalize text-gray-700">{confirmDialog.plan}</span>{' '}
                  plan for 1 month.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-md bg-gray-50 px-4 py-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Current balance</span>
                  <span className="font-medium">{creditBalance.toLocaleString()} credits</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span className="text-gray-500">Cost</span>
                  <span className="font-medium text-red-600">
                    -{confirmDialog.creditsNeeded?.toLocaleString()} credits
                  </span>
                </div>
                <div className="mt-1 flex justify-between border-t pt-1">
                  <span className="text-gray-500">Remaining</span>
                  <span className="font-medium">
                    {(creditBalance - (confirmDialog.creditsNeeded ?? 0)).toLocaleString()} credits
                  </span>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={executePayWithCredits}
                  className="rounded-md bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
                >
                  Confirm &amp; Subscribe
                </button>
              </DialogFooter>
            </>
          )}

          {confirmDialog?.type === 'cancel' && (
            <>
              <DialogHeader>
                <DialogTitle>Cancel Subscription</DialogTitle>
                <DialogDescription>
                  Are you sure you want to cancel your subscription? You will retain access to your
                  current plan until the end of the billing period.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={executeCancelSubscription}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                >
                  Cancel Subscription
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
